import React from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { alpha, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import handleOpenWhatsApp from 'services/whatsappService';
import TnoMediaLabLogo, { MEDIA_LAB_TAGLINES, CherryDevWordmark } from 'components/TnoMediaLabLogo';
import FloatingDecor from '../../CherrySoftware/components/FloatingDecor';
import { hero, WHATSAPP_DEFAULT, WHATSAPP_PHONE } from '../cherryWebContent';

const HERO_OUTER_PADDING_X = { xs: 2.5, sm: 3.5, md: 4.5 };
const HERO_OUTER_PADDING_Y = { xs: 2, sm: 3, md: 4 };
const PANEL_PADDING_X = { xs: 3, sm: 4, md: 5 };
const PANEL_PADDING_Y = { xs: 3, sm: 4, md: 5 };

const openWhatsApp = () => handleOpenWhatsApp(null, WHATSAPP_DEFAULT, WHATSAPP_PHONE);

const Hero = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  return (
    <Box
      position="relative"
      overflow="hidden"
      minHeight={{ md: '88vh' }}
      display="flex"
      alignItems="center"
      paddingX={HERO_OUTER_PADDING_X}
      paddingY={HERO_OUTER_PADDING_Y}
      sx={{
        background: `
          radial-gradient(ellipse 80% 60% at 15% 20%, ${alpha(theme.palette.primary.main, 0.22)} 0%, transparent 55%),
          radial-gradient(ellipse 70% 50% at 90% 10%, ${alpha(theme.palette.secondary.main, 0.18)} 0%, transparent 50%),
          radial-gradient(ellipse 50% 40% at 70% 90%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 45%),
          linear-gradient(160deg, ${theme.palette.alternate.dark} 0%, ${theme.palette.background.default} 45%, ${theme.palette.background.paper} 100%)
        `,
      }}
    >
      <FloatingDecor
        icons={[
          { id: 'web', icon: 'web', top: '14%', right: '10%', size: 44, color: 'primary', animate: true },
          { id: 'devices', icon: 'devices', bottom: '20%', right: '16%', size: 36, color: 'secondary', animate: true, delay: '1.5s' },
        ]}
      />

      <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center" position="relative" zIndex={1}>
        <Grid item xs={12} md={7}>
          <Box
            data-aos={isMd ? 'fade-right' : 'fade-up'}
            paddingX={PANEL_PADDING_X}
            paddingY={PANEL_PADDING_Y}
          >
            <TnoMediaLabLogo
              variant="cherry"
              tagline={MEDIA_LAB_TAGLINES.web}
              alt={hero.brand}
              height={{ xs: 52, md: 68 }}
              width="auto"
              sx={{ mb: { xs: 3, md: 4 } }}
            />

            <Box display="flex" flexWrap="wrap" marginBottom={3} style={{ gap: 8 }}>
              {hero.highlights.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.common.white, 0.92),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                  }}
                />
              ))}
            </Box>

            <Typography
              variant="h1"
              color="textPrimary"
              marginBottom={3}
              sx={{
                fontWeight: 800,
                lineHeight: 1.12,
                fontSize: { xs: '1.85rem', sm: '2.35rem', md: '2.75rem' },
              }}
            >
              {hero.title}
            </Typography>

            <Typography
              component="p"
              color="textSecondary"
              marginBottom={4}
              sx={{
                fontWeight: 400,
                lineHeight: 1.75,
                fontSize: { xs: '1.05rem', md: '1.15rem' },
                maxWidth: 560,
              }}
            >
              {hero.subtitle}
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<WhatsAppIcon />}
              onClick={openWhatsApp}
              sx={{
                px: 4,
                py: 1.7,
                borderRadius: 2,
                fontWeight: 800,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
                boxShadow: `0 14px 36px ${alpha(theme.palette.secondary.main, 0.4)}`,
              }}
            >
              {hero.cta}
            </Button>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 2.5, fontWeight: 600, letterSpacing: 0.2 }}
            >
              {hero.microCopy}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            data-aos={isMd ? 'fade-left' : 'fade-up'}
            borderRadius={4}
            bgcolor={alpha(theme.palette.common.white, 0.82)}
            border={`1px solid ${alpha(theme.palette.primary.main, 0.12)}`}
            boxShadow={`0 28px 64px ${alpha(theme.palette.common.black, 0.06)}`}
            paddingX={PANEL_PADDING_X}
            paddingY={PANEL_PADDING_Y}
            style={{ backdropFilter: 'blur(12px)' }}
          >
            <Typography
              variant="caption"
              component="div"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                fontWeight: 800,
                color: 'secondary.main',
                px: 1.5,
                py: 0.75,
                mb: 2.5,
                borderRadius: 999,
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                gap: 0.5,
              }}
            >
              <CherryDevWordmark color="inherit" />
              <Box component="span"> · Webs & Tiendas</Box>
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, lineHeight: 1.3 }}>
              Deja de vender solo por DM
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary', lineHeight: 1.9 }}>
              <li>Catálogo o servicios en un solo lugar</li>
              <li>WhatsApp y formularios listos</li>
              <li>Tú controlas textos, fotos y precios</li>
              <li>Entrega express en días, no meses</li>
            </Box>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              href="#paquetes"
              sx={{ mt: 3.5, py: 1.35, fontWeight: 700, borderRadius: 2 }}
            >
              Ver planes e inversión
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Hero;
